// src/components/UserProfileModal.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { COLORS } from '../theme';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';

const UserProfileModal = ({ targetUserId, currentUserId, onClose, onFollow, onMessage, onBlock }) => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // ★ 追加：ブロック状態を管理するローカルステータス
    // （※将来的にDBからブロックリストを取得して初期値を設定しますが、今回はUIの挙動として追加します）
    const [isBlocked, setIsBlocked] = useState(false); 

    useEffect(() => {
        if (!targetUserId) return;
        
        const fetchPublicProfile = async () => {
            setLoading(true);
            try {
                // 安全な users_public コレクションからデータを取得
                const docRef = doc(db, 'users_public', targetUserId);
                const docSnap = await getDoc(docRef);
                
                if (docSnap.exists()) {
                    setProfile(docSnap.data());
                } else {
                    toast.error('プロフィールが見つかりませんでした');
                    onClose();
                }
            } catch (error) {
                console.error("プロフィール取得エラー:", error);
                toast.error('データの取得に失敗しました');
            } finally {
                setLoading(false);
            }
        };

        fetchPublicProfile();
    }, [targetUserId, onClose]);

    // 背景をクリックしたときに閉じる処理
    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // ★ 追加：ブロック / 解除の切り替えハンドラー
    const handleBlockToggle = () => {
        if (isBlocked) {
            Swal.fire({
                title: 'ブロック解除',
                text: 'ブロックを解除して、メッセージの送受信を再開できるようにしますか？',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: '解除する',
                confirmButtonColor: COLORS.primary
            }).then((result) => {
                if (result.isConfirmed) {
                    setIsBlocked(false);
                    toast.success(`${profile.displayName}さんのブロックを解除しました`);
                }
            });
        } else {
            Swal.fire({
                title: 'ブロックしますか？',
                text: 'お互いにメッセージの送受信ができなくなります。',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'ブロックする',
                confirmButtonColor: '#d32f2f'
            }).then((result) => {
                if (result.isConfirmed) {
                    setIsBlocked(true);
                    // 親コンポーネントの onBlock を呼ぶ場合は以下を有効化
                    // if (onBlock) onBlock(targetUserId, profile);
                    toast.error(`${profile.displayName}さんをブロックしました`);
                }
            });
        }
    };

    if (loading) {
        return (
            <div style={styles.overlay}>
                <div style={styles.loadingCard}>
                    <div style={{fontSize: '24px', marginBottom: '10px'}}>⏳</div>
                    <div style={{color: '#666', fontWeight: 'bold'}}>読み込み中...</div>
                </div>
            </div>
        );
    }

    if (!profile) return null;

    // 通算成績の計算（表示用）
    const stats = profile.stats || {};
    const games = stats.participations || 0;
    const goals = stats.goals || 0;
    const assists = stats.assists || 0;
    const moms = stats.moms || 0;
    const baseStats = profile.baseStats || { offense: 0, defense: 0, stamina: 0, speed: 0, technique: 0 };

    const getStatColor = (stat) => {
        if(stat === 'offense') return '#ff5252';
        if(stat === 'defense') return '#448aff';
        if(stat === 'stamina') return '#69f0ae';
        if(stat === 'speed') return '#ffeb3b';
        if(stat === 'technique') return '#e040fb';
        return '#ccc';
    };

    return (
        <div style={styles.overlay} onClick={handleOverlayClick}>
            <div style={styles.modalCard}>
                {/* 閉じるボタン */}
                <button onClick={onClose} style={styles.closeBtn}>×</button>

                {/* ヘッダー・アイコン部分 */}
                <div style={styles.header}>
                    <div style={styles.iconContainer}>
                        {profile.pictureUrl ? (
                            <img src={profile.pictureUrl} alt="profile" style={styles.icon} />
                        ) : (
                            <div style={styles.defaultIcon}>👤</div>
                        )}
                        {profile.isRetired && (
                            <div style={styles.retiredBadge}>休部/引退</div>
                        )}
                    </div>
                    <h2 style={styles.name}>{profile.displayName || '名無し'}</h2>
                    
                    <div style={styles.positionInfo}>
                        {profile.mainPosition !== '未設定' ? (
                            <span style={styles.positionBadge}>{profile.mainPosition} ({profile.subPosition})</span>
                        ) : (
                            <span style={{...styles.positionBadge, background: '#eee', color: '#999', border: '1px solid #ccc'}}>ポジション未設定</span>
                        )}
                        <span style={styles.numberBadge}>No.{profile.number || '-'}</span>
                    </div>
                </div>

                {/* スクロール可能な詳細エリア */}
                <div style={styles.body}>
                    
                    {profile.bio && (
                        <div style={styles.bioSection}>
                            <div style={styles.sectionTitle}>💬 自己紹介</div>
                            <div style={styles.bioText}>{profile.bio}</div>
                        </div>
                    )}

                    <div style={styles.infoGrid}>
                        <div style={styles.infoItem}>
                            <div style={styles.infoLabel}>利き足</div>
                            <div style={styles.infoValue}>{profile.dominantFoot || '未設定'}</div>
                        </div>
                        <div style={styles.infoItem}>
                            <div style={styles.infoLabel}>プレースタイル</div>
                            <div style={styles.infoValue}>{profile.playStyle || '-'}</div>
                        </div>
                        <div style={styles.infoItem}>
                            <div style={styles.infoLabel}>好きなチーム/選手</div>
                            <div style={styles.infoValue}>{profile.favoriteTeam || '-'}</div>
                        </div>
                        {(profile.height || profile.weight) && (
                            <div style={styles.infoItem}>
                                <div style={styles.infoLabel}>体格</div>
                                <div style={styles.infoValue}>
                                    {profile.height ? `${profile.height}cm ` : ''} 
                                    {profile.weight ? `${profile.weight}kg` : ''}
                                </div>
                            </div>
                        )}
                    </div>

                    <div style={styles.statsSection}>
                        <div style={styles.sectionTitle}>📊 通算成績</div>
                        <div style={styles.statsGrid}>
                            <div style={styles.statBox}><span style={styles.statLabel}>出場</span><span style={styles.statNum}>{games}</span></div>
                            <div style={styles.statBox}><span style={styles.statLabel}>ゴール</span><span style={styles.statNum}>{goals}</span></div>
                            <div style={styles.statBox}><span style={styles.statLabel}>アシスト</span><span style={styles.statNum}>{assists}</span></div>
                            <div style={styles.statBox}><span style={styles.statLabel}>MOM</span><span style={styles.statNum}>{moms}</span></div>
                        </div>
                    </div>

                    <div style={styles.baseStatsSection}>
                        <div style={styles.sectionTitle}>🎴 基礎能力</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                            {Object.entries(baseStats).map(([key, val]) => (
                                <div key={key} style={{ background: '#f5f5f5', padding: '5px 10px', borderRadius: '15px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', border: `1px solid ${getStatColor(key)}`}}>
                                    <span style={{ color: getStatColor(key), fontWeight: 'bold' }}>{key.toUpperCase().slice(0,3)}</span>
                                    <span style={{ fontWeight: 'bold', color: '#333' }}>{val}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* アクションボタン（自分以外の時だけ表示） */}
                {currentUserId !== targetUserId && (
                    <div style={styles.footer}>
                        {/* メインアクション：フォロー＆メッセージ */}
                        <div style={{ display: 'flex', gap: '10px', width: '100%', marginBottom: '10px' }}>
                            <button 
                                onClick={() => onFollow && onFollow(targetUserId, profile)} 
                                style={{...styles.actionBtn, background: '#e3f2fd', color: COLORS.primary, border: `1px solid ${COLORS.primary}`}}
                            >
                                👤 フォローする
                            </button>
                            <button 
                                onClick={() => onMessage && onMessage(targetUserId, profile)} 
                                style={{...styles.actionBtn, background: COLORS.primary, color: 'white', border: 'none'}}
                            >
                                ✉️ メッセージ
                            </button>
                        </div>
                        
                        {/* サブアクション：ブロック（控えめなデザインに変更） */}
                        <div style={{ textAlign: 'right' }}>
                            <button 
                                onClick={handleBlockToggle} 
                                style={{
                                    background: 'none', border: 'none', fontSize: '12px', 
                                    color: isBlocked ? '#999' : '#d32f2f', 
                                    cursor: 'pointer', textDecoration: 'underline',
                                    display: 'inline-flex', alignItems: 'center', gap: '4px'
                                }}
                            >
                                {isBlocked ? '⭕️ ブロック解除' : '🚫 ブロックする'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const styles = {
    overlay: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: '20px', backdropFilter: 'blur(3px)'
    },
    loadingCard: {
        background: 'white', padding: '30px', borderRadius: '15px',
        textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
    },
    modalCard: {
        background: 'white', width: '100%', maxWidth: '400px',
        maxHeight: '90vh', borderRadius: '20px', overflow: 'hidden',
        display: 'flex', flexDirection: 'column', position: 'relative',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        animation: 'fadeInUp 0.3s ease-out'
    },
    closeBtn: {
        position: 'absolute', top: '15px', right: '15px',
        background: 'rgba(0,0,0,0.1)', color: '#333', border: 'none',
        width: '30px', height: '30px', borderRadius: '50%',
        fontSize: '18px', fontWeight: 'bold', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10
    },
    header: {
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
        padding: '30px 20px 20px', textAlign: 'center', borderBottom: '1px solid #ddd'
    },
    iconContainer: {
        position: 'relative', width: '80px', height: '80px', margin: '0 auto 10px'
    },
    icon: {
        width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover',
        border: `3px solid ${COLORS.primary}`, backgroundColor: 'white'
    },
    defaultIcon: {
        width: '100%', height: '100%', borderRadius: '50%', background: '#ddd',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '35px', border: `3px solid #ccc`
    },
    retiredBadge: {
        position: 'absolute', bottom: '-5px', left: '50%', transform: 'translateX(-50%)',
        background: '#d32f2f', color: 'white', fontSize: '10px', fontWeight: 'bold',
        padding: '2px 8px', borderRadius: '10px', whiteSpace: 'nowrap', border: '2px solid white'
    },
    name: {
        margin: '0 0 5px 0', fontSize: '20px', color: '#333'
    },
    positionInfo: {
        display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center'
    },
    positionBadge: {
        background: '#e3f2fd', color: COLORS.primary, border: `1px solid #90caf9`,
        fontSize: '12px', padding: '3px 10px', borderRadius: '12px', fontWeight: 'bold'
    },
    numberBadge: {
        background: '#333', color: 'white', fontSize: '12px',
        padding: '3px 10px', borderRadius: '12px', fontWeight: 'bold'
    },
    body: {
        padding: '20px', overflowY: 'auto', flex: 1
    },
    sectionTitle: {
        fontSize: '13px', fontWeight: 'bold', color: '#555',
        borderBottom: `2px solid ${COLORS.secondary}`, paddingBottom: '5px', marginBottom: '10px'
    },
    bioSection: {
        marginBottom: '20px'
    },
    bioText: {
        fontSize: '13px', color: '#444', lineHeight: '1.6', whiteSpace: 'pre-wrap',
        background: '#f9f9f9', padding: '10px', borderRadius: '8px'
    },
    infoGrid: {
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px'
    },
    infoItem: {
        background: '#fff', border: '1px solid #eee', padding: '8px', borderRadius: '8px'
    },
    infoLabel: {
        fontSize: '10px', color: '#999', marginBottom: '2px'
    },
    infoValue: {
        fontSize: '12px', color: '#333', fontWeight: 'bold'
    },
    statsSection: {
        marginBottom: '20px'
    },
    statsGrid: {
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px'
    },
    statBox: {
        background: '#f0f4f8', padding: '8px 5px', borderRadius: '8px',
        textAlign: 'center', display: 'flex', flexDirection: 'column'
    },
    statLabel: {
        fontSize: '9px', color: '#666', marginBottom: '2px'
    },
    statNum: {
        fontSize: '16px', fontWeight: 'bold', color: COLORS.primary
    },
    baseStatsSection: {
        marginBottom: '10px'
    },
    footer: {
        padding: '15px 20px', borderTop: '1px solid #eee', background: '#fafafa',
        display: 'flex', flexDirection: 'column' // ★ 縦並びに変更
    },
    actionBtn: {
        flex: 1, padding: '12px', borderRadius: '25px', fontSize: '13px',
        fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center',
        justifyContent: 'center', gap: '5px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
    }
};

export default UserProfileModal;